import { useState, useRef, useCallback, useEffect, type FormEvent, type ClipboardEvent as ReactClipboardEvent } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'motion/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Checkbox, Field, TextArea } from '@/shared/ui';
import { useAuth } from '@/features/auth/context/AuthContext';
import {
  PHONE_COUNTRIES,
  flagEmoji,
  getPhoneCountry,
  type PhoneCountryCode,
} from '@/features/auth/data/phoneCountries';

type Mode = 'login' | 'register';
type Phase = 'credentials' | '2fa';

const RECOVERY_CODE_COUNT = 8;
const EMPTY_CODES = () => Array<string>(RECOVERY_CODE_COUNT).fill('');

function collectWaitlistMetadata() {
  const nav = window.navigator;
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: nav.language,
    languages: Array.from(nav.languages ?? []),
    referrer: document.referrer || undefined,
    userAgent: nav.userAgent,
    platform: nav.platform,
    screen: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`,
  };
}

interface PhoneNumberFieldProps {
  countryCode: PhoneCountryCode;
  value: string;
  onCountryChange: (countryCode: PhoneCountryCode) => void;
  onChange: (value: string) => void;
}

function PhoneNumberField({
  countryCode,
  value,
  onCountryChange,
  onChange,
}: PhoneNumberFieldProps) {
  const selected = getPhoneCountry(countryCode);

  return (
    <div className="space-y-1.5">
      <label className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-ink-500">
        Phone number
      </label>
      <div className="flex h-12 rounded-xl border border-white/60 bg-white/45 backdrop-blur-md overflow-hidden transition-colors focus-within:border-[color:var(--accent-border)] focus-within:bg-white/70 focus-within:shadow-[0_0_0_4px_var(--accent-soft)]">
        <select
          aria-label="Phone country"
          value={countryCode}
          onChange={(event) => onCountryChange(event.target.value as PhoneCountryCode)}
          className="w-[132px] shrink-0 border-r border-white/50 bg-white/40 px-3 text-[13px] font-semibold text-ink-800 outline-none cursor-pointer"
        >
          {PHONE_COUNTRIES.map((country) => (
            <option key={country.code} value={country.code}>
              {flagEmoji(country.code)} {country.name} {country.dialCode}
            </option>
          ))}
        </select>
        <input
          type="tel"
          autoComplete="tel-national"
          required
          value={value}
          onChange={(event) => onChange(event.target.value.replace(/[^\d\s().-]/g, '').slice(0, 24))}
          placeholder="Phone number"
          className="min-w-0 flex-1 px-3.5 text-[14px] text-ink-900 outline-none placeholder:text-ink-400"
        />
      </div>
      <p className="text-[11.5px] text-ink-400">
        Auto-selected country: {selected.name}. We’ll save as {selected.dialCode} plus your number.
      </p>
    </div>
  );
}

/**
 * A hand-off from another page (HDR Studio's Sign Up / 2FA, L7 2026-11-02, lane L5 M4): `/?mode=register`
 * or route state `{ mode, email, name }` opens the form in that mode with those fields filled. Never a password.
 */
function readHandoff(search: string, state: unknown): { mode: Mode; email: string; name: string } {
  const st = (state && typeof state === 'object' ? state : {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v.slice(0, 320) : '');
  const q = new URLSearchParams(search).get('mode');
  const mode: Mode = st.mode === 'register' || q === 'register' ? 'register' : 'login';
  return { mode, email: str(st.email), name: str(st.name) };
}

export function SignInForm() {
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verify2FA, register } = useAuth();
  const [handoff] = useState(() => readHandoff(location.search, location.state));

  const [mode, setMode] = useState<Mode>(handoff.mode);
  const [phase, setPhase] = useState<Phase>('credentials');
  const [name, setName] = useState(handoff.name);
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState<PhoneCountryCode>('IN');
  const [jobTitle, setJobTitle] = useState('');
  const [applicationNotes, setApplicationNotes] = useState('');
  const [email, setEmail] = useState(handoff.email);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>(EMPTY_CODES);
  const recoveryRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setRecoveryRef = useCallback((idx: number) => (el: HTMLInputElement | null) => {
    recoveryRefs.current[idx] = el;
  }, []);

  const handleRecoveryChange = useCallback((idx: number, value: string) => {
    const sanitized = value.replace(/[^a-f0-9-]/gi, '').toLowerCase().slice(0, 9);
    setRecoveryCodes((prev) => {
      const next = [...prev];
      next[idx] = sanitized;
      return next;
    });
    if (sanitized.length === 9 && idx < RECOVERY_CODE_COUNT - 1) {
      recoveryRefs.current[idx + 1]?.focus();
    }
  }, []);

  const handleRecoveryPaste = useCallback((e: ReactClipboardEvent) => {
    const pasted = e.clipboardData.getData('text');
    const codes = pasted
      .split(/[\n,]+/)
      .map((c) => c.trim().toLowerCase())
      .filter((c) => /^[a-f0-9]{4}-[a-f0-9]{4}$/.test(c));
    if (codes.length >= 2) {
      e.preventDefault();
      setRecoveryCodes((prev) => {
        const next = [...prev];
        codes.forEach((code, i) => {
          if (i < RECOVERY_CODE_COUNT) next[i] = code;
        });
        return next;
      });
      const focusIdx = Math.min(codes.length, RECOVERY_CODE_COUNT - 1);
      setTimeout(() => recoveryRefs.current[focusIdx]?.focus(), 0);
    }
  }, []);

  const allRecoveryFilled = recoveryCodes.every((c) => c.length === 9);

  useEffect(() => {
    let cancelled = false;

    async function preselectCountryFromIp() {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) return;
        const data = await response.json() as { country_code?: string };
        const detected = data.country_code?.toUpperCase();
        if (
          !cancelled &&
          detected &&
          PHONE_COUNTRIES.some((country) => country.code === detected)
        ) {
          setPhoneCountryCode(detected as PhoneCountryCode);
        }
      } catch {
        // Keep the default country if geo lookup is blocked or unavailable.
      }
    }

    if (mode === 'register') {
      void preselectCountryFromIp();
    }

    return () => {
      cancelled = true;
    };
  }, [mode]);

  const resetWaitlistFields = () => {
    setCompanyName('');
    setPhone('');
    setPhoneCountryCode('IN');
    setJobTitle('');
    setApplicationNotes('');
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setPhase('credentials');
    setError(null);
    setTotpCode('');
    setTempToken('');
    setUseRecovery(false);
    setRecoveryCodes(EMPTY_CODES);
    if (next === 'login') resetWaitlistFields();
  };

  const handleCredentialSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (mode === 'login' && (!email || !password)) return;
    if (
      mode === 'register' &&
      (!name || !email || !password || !companyName || !phone.replace(/[^\d]/g, '') || !jobTitle)
    ) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (mode === 'login') {
        const result = await login(email, password);
        if (result.twoFactorRequired) {
          setTempToken(result.tempToken);
          setPhase('2fa');
          setTotpCode('');
          setUseRecovery(false);
          setRecoveryCodes(EMPTY_CODES);
        }
      } else {
        const selectedPhoneCountry = getPhoneCountry(phoneCountryCode);
        const normalizedPhone = phone.replace(/[^\d]/g, '');
        await register({
          name,
          email,
          password,
          companyName,
          phone: `${selectedPhoneCountry.dialCode}${normalizedPhone}`,
          phoneCountry: selectedPhoneCountry.code,
          phoneDialCode: selectedPhoneCountry.dialCode,
          jobTitle,
          applicationNotes: applicationNotes.trim() || undefined,
          clientMetadata: collectWaitlistMetadata(),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handle2FASubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (useRecovery && !allRecoveryFilled) return;
    if (!useRecovery && !totpCode.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      let result;
      if (useRecovery) {
        result = await verify2FA(tempToken, { recoveryCodes });
      } else {
        result = await verify2FA(tempToken, { code: totpCode.trim() });
      }
      if (result.twoFactorReset) {
        sessionStorage.setItem('2fa_reset', '1');
        navigate('/app/settings?tab=security', { replace: true });
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const backToCredentials = () => {
    setPhase('credentials');
    setTempToken('');
    setTotpCode('');
    setError(null);
    setUseRecovery(false);
    setRecoveryCodes(EMPTY_CODES);
  };

  const isLogin = mode === 'login';

  if (phase === '2fa') {
    return (
      <motion.div
        key="2fa"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="surface-card w-full max-w-[440px] p-7 sm:p-9"
      >
        <header className="mb-7">
          <div className="flex items-center gap-3 mb-4">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700">
              <ShieldCheck size={20} strokeWidth={2.2} />
            </span>
          </div>
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
            Two-factor authentication
          </p>
          <h2 className="font-headline text-[28px] leading-[1.1] text-ink-900">
            {useRecovery ? 'Enter your recovery codes' : 'Enter your verification code'}
          </h2>
          <p className="mt-2.5 text-[14px] text-ink-500 leading-relaxed">
            {useRecovery
              ? 'Enter all 8 recovery codes you saved during setup. You can paste them all at once.'
              : 'Open your authenticator app and enter the 6-digit code for Jewelitt.'}
          </p>
        </header>

        <form onSubmit={handle2FASubmit} className="space-y-4" noValidate>
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {useRecovery ? (
            <div className="space-y-2" onPaste={handleRecoveryPaste}>
              {recoveryCodes.map((code, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-[12px] font-mono text-ink-400 w-5 text-right shrink-0">
                    {idx + 1}.
                  </span>
                  <input
                    ref={setRecoveryRef(idx)}
                    type="text"
                    autoComplete="off"
                    autoFocus={idx === 0}
                    placeholder="xxxx-xxxx"
                    value={code}
                    onChange={(e) => handleRecoveryChange(idx, e.target.value)}
                    className={`w-full h-10 px-3 rounded-lg border font-mono text-[14px] tracking-wider transition-colors outline-none backdrop-blur-md focus:ring-2 focus:ring-gold-300 ${
                      code.length === 9
                        ? 'border-emerald-300 bg-emerald-50/50 text-emerald-800'
                        : 'border-white/60 bg-white/45 text-ink-800 hover:border-white/80'
                    }`}
                  />
                  {code.length === 9 && (
                    <span className="text-emerald-500 text-[14px] shrink-0">&#10003;</span>
                  )}
                </div>
              ))}
              <p className="text-[12px] text-ink-400 mt-1 pl-8">
                Each code is 9 characters in the format xxxx-xxxx. Paste all codes at once or type them one by one.
              </p>
            </div>
          ) : (
            <Field
              label="6-digit code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          )}

          <motion.button
            whileHover={reduceMotion ? undefined : { y: -1 }}
            whileTap={{ scale: 0.99 }}
            type="submit"
            disabled={submitting || (useRecovery ? !allRecoveryFilled : !totpCode.trim())}
            className="btn-press btn-shine w-full h-12 rounded-xl gold-gradient shadow-gold text-white font-semibold text-[14px] tracking-wide flex items-center justify-center gap-2 cursor-pointer focus-ring disabled:opacity-80"
          >
            <span>{submitting ? 'Verifying…' : 'Verify and sign in'}</span>
            <motion.span
              animate={submitting ? { x: [0, 5, 0] } : { x: 0 }}
              transition={{ repeat: submitting ? Infinity : 0, duration: 0.9 }}
              className="inline-flex"
            >
              <ArrowRight size={16} strokeWidth={2.2} />
            </motion.span>
          </motion.button>

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={backToCredentials}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-600 hover:text-ink-900 transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} strokeWidth={2.2} />
              Back to sign in
            </button>

            <button
              type="button"
              onClick={() => {
                setUseRecovery(!useRecovery);
                setTotpCode('');
                setRecoveryCodes(EMPTY_CODES);
                setError(null);
              }}
              className="text-[13px] font-semibold text-ink-600 hover:text-gold-600 transition-colors cursor-pointer"
            >
              {useRecovery ? 'Use authenticator app' : 'Use a recovery code'}
            </button>
          </div>
        </form>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="credentials"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
      className="surface-card w-full max-w-[420px] p-7 sm:p-9"
    >
      <header className="mb-9">
        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
          {isLogin ? 'Sign in' : 'Early access waitlist'}
        </p>
        <h2 className="font-headline text-[34px] leading-[1.1] text-ink-900">
          {isLogin ? 'Welcome back to your studio.' : 'Apply for early access.'}
        </h2>
        <p className="mt-2.5 text-[14.5px] text-ink-500 leading-relaxed">
          {isLogin
            ? 'Pick up where you left off. Drafts, renders and history are all in sync.'
            : 'Tell us about your studio. We review every application and reach out when a spot opens.'}
        </p>
      </header>

      <form onSubmit={handleCredentialSubmit} className="space-y-3.5" noValidate>
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700"
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {!isLogin && (
            <motion.div
              key="waitlist-fields"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="space-y-3.5"
            >
              <Field
                label="Full name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Field
                label="Company name"
                type="text"
                autoComplete="organization"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
              <PhoneNumberField
                countryCode={phoneCountryCode}
                value={phone}
                onCountryChange={setPhoneCountryCode}
                onChange={setPhone}
              />
              <Field
                label="Your role at the company"
                type="text"
                required
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                hint="e.g. Creative Director, Merchandiser, Goldsmith"
              />
            </motion.div>
          )}
        </AnimatePresence>

        <Field
          label="Email address"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="tabular"
        />

        <Field
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete={isLogin ? 'current-password' : 'new-password'}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="grid place-items-center w-8 h-8 rounded-md text-ink-400 hover:text-ink-800 hover:bg-ink-50 transition-colors cursor-pointer focus-ring"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />

        {!isLogin && (
          <TextArea
            label="Notes (optional)"
            rows={3}
            maxLength={1000}
            value={applicationNotes}
            onChange={(e) => setApplicationNotes(e.target.value)}
            hint="Anything else we should know about your studio or use case?"
          />
        )}

        {isLogin && (
          <div className="flex items-center justify-between pt-1 text-[12.5px]">
            <Checkbox label="Keep me signed in" defaultChecked />
            <a href="#" className="font-semibold text-ink-700 hover:text-gold-600 transition-colors">
              Forgot password?
            </a>
          </div>
        )}

        {!isLogin && (
          <p className="pt-1 text-[12px] text-ink-400">
            Choose a password you will use to sign in once your access is approved.
          </p>
        )}

        <motion.button
          whileHover={reduceMotion ? undefined : { y: -1 }}
          whileTap={{ scale: 0.99 }}
          type="submit"
          disabled={submitting}
          className="btn-press btn-shine mt-4 w-full h-12 rounded-xl gold-gradient shadow-gold text-white font-semibold text-[14px] tracking-wide flex items-center justify-center gap-2 cursor-pointer focus-ring disabled:opacity-80"
        >
          <span>
            {submitting
              ? isLogin
                ? 'Signing in…'
                : 'Submitting application…'
              : isLogin
                ? 'Sign in'
                : 'Apply for early access'}
          </span>
          <motion.span
            animate={submitting ? { x: [0, 5, 0] } : { x: 0 }}
            transition={{ repeat: submitting ? Infinity : 0, duration: 0.9 }}
            className="inline-flex"
          >
            <ArrowRight size={16} strokeWidth={2.2} />
          </motion.span>
        </motion.button>

        {isLogin ? (
          <div className="pt-4 space-y-3">
            <div className="relative flex items-center gap-3 py-1">
              <span className="flex-1 h-px bg-ink-200" />
              <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-ink-400">
                New here
              </span>
              <span className="flex-1 h-px bg-ink-200" />
            </div>
            <motion.button
              type="button"
              whileHover={reduceMotion ? undefined : { y: -1 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => switchMode('register')}
              className="btn-press w-full h-14 rounded-xl border border-white/60 bg-white/40 text-ink-900 font-semibold text-[15px] tracking-wide hover:bg-white/60 backdrop-blur-md transition-colors cursor-pointer focus-ring"
            >
              Sign up for waitlist
            </motion.button>
            <p className="text-center text-[12px] text-ink-400 leading-relaxed">
              No public sign-ups yet. Join the waitlist and we will email you when your studio is ready.
            </p>
          </div>
        ) : (
          <p className="text-center text-[13px] text-ink-500 pt-2">
            Already have access?{' '}
            <button
              type="button"
              onClick={() => switchMode('login')}
              className="font-semibold text-ink-800 hover:text-gold-600 transition-colors cursor-pointer"
            >
              Sign in
            </button>
          </p>
        )}
      </form>
    </motion.div>
  );
}
